import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { format } from 'date-fns';
import { X, Upload, Plus, Trash, Loader, FileDown, Check, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { supabase, Entry, Product, updateEntry, createEntry } from '../lib/supabase';
import { uploadToR2, checkStorageCapacity, getDisplayUrl } from '../lib/r2Storage';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ImageModal from './ImageModal';

interface EntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  entries: Entry[];
  onEntrySaved: () => void;
  preselectedEntry?: Entry;
}

interface ProductFormData {
  description: string;
  crates: string;
  pallets: string;
}

export default function EntryModal({
  isOpen,
  onClose,
  date,
  entries: unsortedEntries,
  onEntrySaved,
  preselectedEntry,
}: EntryModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    delivery_note_number: '',
    farmer_name: '',
    customer_name: '',
    notes: '',
    globalgap_confirmed: '',
  });
  const [products, setProducts] = useState<ProductFormData[]>([
    { description: '', crates: '', pallets: '' }
  ]);
  const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);
  const [loading, setLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadCurrent, setUploadCurrent] = useState(0);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);

  // Auto-populate form when preselectedEntry is provided
  useEffect(() => {
    if (preselectedEntry) {
      setSelectedEntry(preselectedEntry);
      setFormData({
        delivery_note_number: preselectedEntry.delivery_note_number || '',
        farmer_name: preselectedEntry.farmer?.name || '',
        customer_name: preselectedEntry.customer?.name || '',
        notes: preselectedEntry.notes || '',
        globalgap_confirmed: preselectedEntry.globalgap_confirmed || '',
      });
      if (preselectedEntry.products && preselectedEntry.products.length > 0) {
        setProducts(
          preselectedEntry.products.map((p) => ({
            description: p.description,
            crates: String(p.crates),
            pallets: String(p.pallets),
          }))
        );
      } else {
        setProducts([{ description: '', crates: '', pallets: '' }]);
      }
      setImageUrls(preselectedEntry.image_urls || []);
    }
  }, [preselectedEntry]);

  // Sort entries by created_at timestamp and keep invoiced items together
  const entries = [...unsortedEntries].sort((a, b) => {
    // First sort by invoice status
    if (a.invoiced !== b.invoiced) {
      return a.invoiced ? -1 : 1;
    }
    // Then sort by creation date
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const allFiles = Array.from(files);
      const totalFiles = allFiles.length;
      setUploadTotal(totalFiles);
      setUploadCurrent(0);

      let totalBytes = 0;
      for (const file of allFiles) {
        totalBytes += file.size;
      }
      await checkStorageCapacity(totalBytes);

      const batchSize = 6;
      const results: string[] = [];
      let completedSteps = 0;
      const totalSteps = totalFiles * 2; // compress + upload per file
      let processedFiles = 0;

      for (let i = 0; i < allFiles.length; i += batchSize) {
        const batch = allFiles.slice(i, i + batchSize);
        const batchPromises = batch.map(async (file) => {
          const compressedFile = await compressImage(file);
          completedSteps++;
          setUploadProgress((completedSteps / totalSteps) * 100);

          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.jpeg`;
          const publicUrl = await uploadToR2(compressedFile, fileName);

          completedSteps++;
          processedFiles++;
          setUploadCurrent(processedFiles);
          setUploadProgress((completedSteps / totalSteps) * 100);

          return publicUrl;
        });

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      }

      setImageUrls(prevUrls => [...prevUrls, ...results]);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success(`Successfully uploaded ${results.length} image${results.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error uploading images:', error);
      const message = error instanceof Error ? error.message : 'Failed to upload images';
      if (message.toLowerCase().includes('storage full')) {
        toast.error('Storage full: not enough space to upload these images');
      } else {
        toast.error(message);
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadCurrent(0);
      setUploadTotal(0);
    }
  }

  const compressionWorkerRef = useRef<Worker | null>(null);

  useEffect(() => {
    compressionWorkerRef.current = new Worker(
      new URL('../workers/imageCompression.worker.ts', import.meta.url),
      { type: 'module' }
    );
    return () => {
      compressionWorkerRef.current?.terminate();
      compressionWorkerRef.current = null;
    };
  }, []);

  async function compressImage(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const worker = compressionWorkerRef.current;
      if (!worker) {
        reject(new Error('Compression worker not available'));
        return;
      }

      const handler = (e: MessageEvent) => {
        worker.removeEventListener('message', handler);
        if (e.data.error) {
          reject(new Error(e.data.error));
        } else {
          resolve(e.data.blob);
        }
      };

      worker.addEventListener('message', handler);
      worker.postMessage({ file, maxDimension: 2048, quality: 0.85 });
    });
  }

  function handleRemoveImage(index: number) {
    setImageUrls(urls => urls.filter((_, i) => i !== index));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      setLoading(true);

      // Get or create farmer
      let farmerId;
      const { data: existingFarmers, error: farmersError } = await supabase
        .from('farmers')
        .select('id')
        .eq('name', formData.farmer_name.trim());

      if (farmersError) throw farmersError;

      if (existingFarmers && existingFarmers.length > 0) {
        farmerId = existingFarmers[0].id;
      } else {
        const { data: newFarmer, error: newFarmerError } = await supabase
          .from('farmers')
          .insert([{ name: formData.farmer_name.trim() }])
          .select()
          .single();

        if (newFarmerError) throw newFarmerError;
        farmerId = newFarmer.id;
      }

      // Get or create customer
      let customerId;
      const { data: existingCustomers, error: customersError } = await supabase
        .from('customers')
        .select('id')
        .eq('name', formData.customer_name.trim());

      if (customersError) throw customersError;

      if (existingCustomers && existingCustomers.length > 0) {
        customerId = existingCustomers[0].id;
      } else {
        const { data: newCustomer, error: newCustomerError } = await supabase
          .from('customers')
          .insert([{ name: formData.customer_name.trim() }])
          .select()
          .single();

        if (newCustomerError) throw newCustomerError;
        customerId = newCustomer.id;
      }

      const entryData = {
        date: format(date, 'yyyy-MM-dd'),
        delivery_note_number: formData.delivery_note_number || null,
        farmer_id: farmerId,
        customer_id: customerId,
        notes: formData.notes || null,
        image_urls: imageUrls,
        globalgap_confirmed: formData.globalgap_confirmed || null,
      };

      let entryId;
      if (selectedEntry) {
        const { error: updateError } = await updateEntry(selectedEntry.id, entryData);
        if (updateError) throw updateError;
        entryId = selectedEntry.id;

        const { error: deleteError } = await supabase
          .from('products')
          .delete()
          .eq('entry_id', entryId);
        if (deleteError) throw deleteError;
      } else {
        const { data: newEntry, error: insertError } = await createEntry({
          ...entryData,
          created_by: user!.id,
        });
        if (insertError) throw insertError;
        entryId = newEntry.id;
      }

      const productsData = products
        .filter(p => p.description && p.crates)
        .map(p => ({
          entry_id: entryId,
          description: p.description,
          crates: Number(p.crates),
          pallets: Number(p.pallets) || 0,
        }));

      if (productsData.length > 0) {
        const { error: productsError } = await supabase
          .from('products')
          .insert(productsData);
        if (productsError) throw productsError;
      }

      toast.success(selectedEntry ? 'Entry updated' : 'Entry created');
      onEntrySaved();
      onClose();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Failed to save entry');
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteEntry(entryId: string) {
    const confirmDelete = window.confirm('Are you sure you want to delete this entry? This action cannot be undone.');
    if (!confirmDelete) return;

    try {
      setDeletingEntryId(entryId);

      // Delete the entry (products will be deleted automatically due to CASCADE)
      const { error } = await supabase
        .from('entries')
        .delete()
        .eq('id', entryId);

      if (error) throw error;

      toast.success('Entry deleted successfully');
      onEntrySaved(); // Refresh the entries list
      
      // If this was the selected entry, clear the selection
      if (selectedEntry?.id === entryId) {
        setSelectedEntry(null);
        setFormData({
          delivery_note_number: '',
          farmer_name: '',
          customer_name: '',
          notes: '',
          globalgap_confirmed: '',
        });
        setProducts([{ description: '', crates: '', pallets: '' }]);
        setImageUrls([]);
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    } finally {
      setDeletingEntryId(null);
    }
  }

  async function handleEntrySelect(entry: Entry) {
    try {
      setSelectedEntry(entry);
      
      const [farmerResult, customerResult, productsResult] = await Promise.all([
        supabase.from('farmers').select('name').eq('id', entry.farmer_id).single(),
        supabase.from('customers').select('name').eq('id', entry.customer_id).single(),
        supabase.from('products').select('*').eq('entry_id', entry.id),
      ]);

      if (farmerResult.error) throw farmerResult.error;
      if (customerResult.error) throw customerResult.error;
      if (productsResult.error) throw productsResult.error;

      setFormData({
        delivery_note_number: entry.delivery_note_number || '',
        farmer_name: farmerResult.data?.name || '',
        customer_name: customerResult.data?.name || '',
        notes: entry.notes || '',
        globalgap_confirmed: entry.globalgap_confirmed || '',
      });

      if (productsResult.data && productsResult.data.length > 0) {
        setProducts(productsResult.data.map(p => ({
          description: p.description,
          crates: String(p.crates),
          pallets: String(p.pallets),
        })));
      } else {
        setProducts([{ description: '', crates: '', pallets: '' }]);
      }

      setImageUrls(entry.image_urls || []);
    } catch (error) {
      console.error('Error fetching entry details:', error);
      toast.error('Failed to load entry details');
    }
  }

  async function handleToggleInvoiced(entry: Entry) {
    if (!entry.invoiced) {
      if (!entry.globalgap_confirmed) {
        toast.error('GlobalGap Accreditation must be confirmed before marking as invoiced');
        return;
      }
    }

    try {
      const { error } = await supabase
        .from('entries')
        .update({ 
          invoiced: !entry.invoiced,
          updated_at: new Date().toISOString()
        })
        .eq('id', entry.id);

      if (error) throw error;

      toast.success(entry.invoiced ? 'Marked as not invoiced' : 'Marked as invoiced');
      onEntrySaved();
    } catch (error) {
      console.error('Error updating invoice status:', error);
      toast.error('Failed to update invoice status');
    }
  }

  function handleAddProduct() {
    setProducts([...products, { description: '', crates: '', pallets: '' }]);
  }

  function handleRemoveProduct(index: number) {
    setProducts(products.filter((_, i) => i !== index));
  }

  function handleProductChange(index: number, field: keyof ProductFormData, value: string) {
    const newProducts = [...products];
    newProducts[index] = { ...newProducts[index], [field]: value };
    setProducts(newProducts);
  }

  async function handleExportPDF(entry: Entry) {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - margin * 2;

      // Title
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Delivery Note', margin, 20);

      // Header details
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const details: [string, string][] = [
        ['Date', format(new Date(entry.date), 'MMMM d, yyyy')],
        ['Delivery Note Number', entry.delivery_note_number || 'N/A'],
        ['Farmer', entry.farmer?.name || 'N/A'],
        ['Customer', entry.customer?.name || 'N/A'],
        ['Invoiced', entry.invoiced ? 'Yes' : 'No'],
        ['GlobalGap Accreditation Confirmed', entry.globalgap_confirmed || 'Not selected'],
      ];

      let y = 35;
      for (const [label, value] of details) {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.text(value, margin + 80, y);
        y += 10;
      }

      // Products table
      if (entry.products && entry.products.length > 0) {
        autoTable(doc, {
          startY: y + 5,
          head: [['Description', 'Crates', 'Pallets']],
          body: entry.products.map(product => [
            product.description,
            product.crates.toString(),
            product.pallets.toString()
          ]),
          styles: { fontSize: 10 },
          headStyles: { fillColor: [59, 130, 246] },
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      } else {
        y += 5;
      }

      // Notes
      if (entry.notes) {
        doc.setFont('helvetica', 'bold');
        doc.text('Notes:', margin, y);
        y += 8;
        doc.setFont('helvetica', 'normal');
        const splitNotes = doc.splitTextToSize(entry.notes, contentWidth);
        doc.text(splitNotes, margin, y);
        y += splitNotes.length * 6 + 8;
      }

      // Images
      if (entry.image_urls && entry.image_urls.length > 0) {
        doc.setFont('helvetica', 'bold');

        // Ensure there's room for the heading before starting images
        if (y > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          y = 20;
        }

        doc.text('Images:', margin, y);
        y += 8;
        doc.setFont('helvetica', 'normal');

        const imgMaxWidth = contentWidth;
        const imgMaxHeight = 120;

        for (const url of entry.image_urls) {
          try {
            // Fetch as blob and convert to base64
            const response = await fetch(getDisplayUrl(url));
            const blob = await response.blob();
            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });

            // Get natural dimensions via an Image element
            const imgDims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
              const img = new Image();
              img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
              img.onerror = reject;
              img.src = base64;
            });

            // Scale to fit within max dimensions
            let drawW = imgMaxWidth;
            let drawH = (imgDims.h / imgDims.w) * drawW;
            if (drawH > imgMaxHeight) {
              drawH = imgMaxHeight;
              drawW = (imgDims.w / imgDims.h) * drawH;
            }

            // Add new page if image won't fit
            if (y + drawH > doc.internal.pageSize.getHeight() - margin) {
              doc.addPage();
              y = 20;
            }

            const format = base64.startsWith('data:image/png') ? 'PNG' : 'JPEG';
            doc.addImage(base64, format, margin, y, drawW, drawH);
            y += drawH + 10;
          } catch {
            // Skip images that fail to load
          }
        }
      }

      doc.save(`delivery-note-${entry.delivery_note_number || format(new Date(entry.date), 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => { if (!selectedImage) onClose(); }}
        className="fixed inset-0 z-40 overflow-y-auto"
      >
        <div className="min-h-screen px-4 text-center">
          <Dialog.Overlay className="fixed inset-0 bg-black/30" />

          <span className="inline-block h-screen align-middle">&#8203;</span>

          <div className={clsx(
            "inline-block w-full text-left align-middle transition-all transform bg-white shadow-xl",
            "h-screen sm:h-auto overflow-y-auto",
            "p-4 sm:p-6",
            "my-0 sm:my-8",
            "rounded-none sm:rounded-lg",
            "max-w-none sm:max-w-2xl"
          )}>
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {format(date, 'MMMM d, yyyy')}
              </Dialog.Title>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              {entries.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Existing Entries
                  </h3>
                  <div className="space-y-2">
                    {entries.map((entry) => (
                      <div
                        key={entry.id}
                        className={`p-3 rounded-md cursor-pointer transition-colors ${
                          entry.invoiced ? 'bg-green-50' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div
                            onClick={() => handleEntrySelect(entry)}
                            className="flex-1"
                          >
                            <div className="font-medium">DN: {entry.delivery_note_number || 'No DN'}</div>
                            <div className="text-sm text-gray-500">
                              {entry.farmer?.name} → {entry.customer?.name}
                            </div>
                            {entry.products && entry.products.length > 0 && (
                              <div className="mt-1 text-sm text-gray-500">
                                {entry.products.map((product, idx) => (
                                  <div key={idx}>
                                    {product.description}: {product.crates} crates
                                    {product.pallets > 0 && `, ${product.pallets} pallets`}
                                  </div>
                                ))}
                              </div>
                            )}
                            {entry.notes && (
                              <div className="mt-2 text-sm text-gray-600 italic">
                                Notes: {entry.notes}
                              </div>
                            )}
                            <div className="mt-1 text-xs">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                                entry.globalgap_confirmed === 'YES'
                                  ? 'bg-green-100 text-green-800'
                                  : entry.globalgap_confirmed === 'NO'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                GG: {entry.globalgap_confirmed || '—'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                            <button
                              onClick={() => handleToggleInvoiced(entry)}
                              className={`p-2 rounded-full ${
                                entry.invoiced
                                  ? 'text-green-600 hover:bg-green-100'
                                  : 'text-gray-400 hover:bg-gray-200'
                              }`}
                              title={entry.invoiced ? 'Mark as not invoiced' : 'Mark as invoiced'}
                            >
                              <Check className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteEntry(entry.id)}
                              disabled={deletingEntryId === entry.id}
                              className="p-2 text-red-600 hover:text-red-900 rounded-full hover:bg-red-100 disabled:opacity-50"
                              title="Delete entry"
                            >
                              {deletingEntryId === entry.id ? (
                                <Loader className="h-5 w-5 animate-spin" />
                              ) : (
                                <Trash2 className="h-5 w-5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleExportPDF(entry)}
                              className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-200"
                              title="Export as PDF"
                            >
                              <FileDown className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                        {entry.image_urls && entry.image_urls.length > 0 && (
                          <div className="mt-2 grid grid-cols-4 gap-2">
                            {entry.image_urls.map((url, index) => (
                              <img
                                key={index}
                                src={getDisplayUrl(url)}
                                alt={`Photo ${index + 1}`}
                                className="w-full h-20 object-cover rounded-lg cursor-pointer hover:opacity-75 transition-opacity"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedImage(url);
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Delivery Note Number
                  </label>
                  <input
                    type="text"
                    value={formData.delivery_note_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        delivery_note_number: e.target.value,
                      })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Farmer Name
                  </label>
                  <input
                    type="text"
                    value={formData.farmer_name}
                    onChange={(e) =>
                      setFormData({ ...formData, farmer_name: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                    placeholder="Enter farmer name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) =>
                      setFormData({ ...formData, customer_name: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                    placeholder="Enter customer name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    GlobalGap Accreditation Confirmed
                  </label>
                  <select
                    value={formData.globalgap_confirmed}
                    onChange={(e) =>
                      setFormData({ ...formData, globalgap_confirmed: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="YES">YES</option>
                    <option value="NO">NO</option>
                  </select>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Products
                    </label>
                    <button
                      type="button"
                      onClick={handleAddProduct}
                      className="inline-flex items-center px-2 py-1 border border-transparent text-sm font-medium rounded text-indigo-600 hover:text-indigo-500"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Product
                    </button>
                  </div>
                  <div className="space-y-2">
                    {products.map((product, index) => (
                      <div key={index} className="flex flex-col sm:flex-row gap-2 items-start">
                        <div className="w-full sm:flex-1">
                          <input
                            type="text"
                            value={product.description}
                            onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                            placeholder="Description"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>
                        <div className="w-full sm:w-24">
                          <input
                            type="number"
                            value={product.crates}
                            onChange={(e) => handleProductChange(index, 'crates', e.target.value)}
                            placeholder="Crates"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>
                        <div className="w-full sm:w-24">
                          <input
                            type="number"
                            value={product.pallets}
                            onChange={(e) => handleProductChange(index, 'pallets', e.target.value)}
                            placeholder="Pallets"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          />
                        </div>
                        {products.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveProduct(index)}
                            className="p-2 text-gray-400 hover:text-gray-500 self-start"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Photos
                  </label>
                  <div className="mt-1">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                      {imageUrls.map((url, index) => (
                        <div key={index} className="relative">
                          <img
                            src={getDisplayUrl(url)}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-75 transition-opacity"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedImage(url);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg text-gray-600 hover:text-gray-800"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className={`h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-600 hover:border-gray-400 hover:text-gray-500 ${
                          isUploading ? 'cursor-not-allowed border-indigo-300 bg-indigo-50' : ''
                        }`}
                      >
                        {isUploading ? (
                          <div className="flex flex-col items-center justify-center">
                            <div className="relative w-14 h-14">
                              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 64 64">
                                <circle
                                  cx="32"
                                  cy="32"
                                  r="26"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="5"
                                  className="text-indigo-200"
                                />
                                <circle
                                  cx="32"
                                  cy="32"
                                  r="26"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="5"
                                  strokeLinecap="round"
                                  strokeDasharray={163.36}
                                  strokeDashoffset={163.36 - (163.36 * uploadProgress) / 100}
                                  className="text-indigo-600 transition-all duration-300 ease-out"
                                />
                              </svg>
                              <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-indigo-700">
                                {Math.round(uploadProgress)}%
                              </span>
                            </div>
                            <span className="mt-1 text-xs text-indigo-600 font-medium">
                              {uploadCurrent} of {uploadTotal}
                            </span>
                          </div>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 mb-2" />
                            <span>Add Photos</span>
                          </>
                        )}
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      multiple
                      disabled={isUploading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {loading
                      ? 'Saving...'
                      : selectedEntry
                      ? 'Update Entry'
                      : 'Create Entry'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Dialog>

      {selectedImage && (
        <ImageModal
          isOpen={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          imageUrl={selectedImage}
        />
      )}
    </>
  );
}